import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/orders')
const ALL_ORDERS_ENDPOINT = `${API_BASE_URL}/approved`
const PUBLISHED_ORDERS_ENDPOINT = `${API_BASE_URL}/published`
const PUBLISHED_TOTAL_REVENUE_ENDPOINT = `${API_BASE_URL}/totalRevenue`
const DELETE_ORDER_ENDPOINT = `${API_BASE_URL}/deleteOrder`
const PUBLISHED_ORDER_BOOKS_ENDPOINT = `${API_BASE_URL}/publishedOrderBooks`
const SAVE_ORDER_ENDPOINT = `${API_BASE_URL}/add`
const GET_ORDER_BY_ID_ENDPOINT = `${API_BASE_URL}/getOrder`
const UPDATE_ORDER_ENDPOINT = `${API_BASE_URL}/updateOrder`
const CHANGE_ORDER_STATUS_ENDPOINT = `${API_BASE_URL}/changeOrderStatus`
const CHANGE_ORDER_CUSTOMER_ENDPOINT = `${API_BASE_URL}/changeOrderCustomer`
const GET_ORDER_PAYMENT_PLAN_ENDPOINT = `${API_BASE_URL}/getOrderPaymentPlan`
const ADD_ORDER_PAYMENT_PLAN_ENDPOINT = `${API_BASE_URL}/addPaymentPlan`
const GET_ORDER_RETURNS_ENDPOINT = `${API_BASE_URL}/getOrderReturns`
const GET_ORDER_PAYMENTS_ENDPOINT = `${API_BASE_URL}/getOrderPayments`
const ADD_ORDER_PAYMENT_ENDPOINT = `${API_BASE_URL}/addPayment`
const UPDATE_ORDER_PAYMENT_ENDPOINT = `${API_BASE_URL}/updatePayment`
const DELETE_ORDER_PAYMENT_ENDPOINT = `${API_BASE_URL}/deletePayment`
const RELEASE_ORDER_ENDPOINT = `${API_BASE_URL}/releaseOrder`
const RETURN_ORDER_ENDPOINT = `${API_BASE_URL}/returnOrder`
async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })

  if (!response.ok) {
    let message = `Order request failed (${response.status}) at ${url}`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }

  return response.status === 204 ? null : response.json()
}

function findOrders(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of ['orders', 'orderList', 'results', 'items', 'data']) {
    const orders = findOrders(data[key])
    if (orders.length || Array.isArray(data[key])) return orders
  }

  return []
}

function findBooks(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of [
    'books',
    'orderBooks',
    'bookList',
    'booklist',
    'publishedBooks',
    'published_books',
    'results',
    'items',
    'rows',
    'result',
    'payload',
    'data',
  ]) {
    const books = findBooks(data[key])
    if (books.length) return books
  }

  for (const value of Object.values(data)) {
    const books = findBooks(value)
    if (books.length && books.every((item) => item && typeof item === 'object'))
      return books
  }

  return []
}

function findNextCursor(data) {
  if (!data || typeof data !== 'object') return null
  return (
    data.nextCursor ??
    data.next_cursor ??
    data.nextPageToken ??
    data.next_page_token ??
    data.next ??
    findNextCursor(data.orders) ??
    findNextCursor(data.orderList) ??
    findNextCursor(data.results) ??
    findNextCursor(data.items) ??
    findNextCursor(data.data) ??
    findNextCursor(data.pagination) ??
    findNextCursor(data.meta) ??
    null
  )
}

const addParam = (params, key, value) => {
  params.set(key, value === undefined || value === null ? '' : String(value))
}

export async function getAllApprovedOrders() {
  const data = await request(`${ALL_ORDERS_ENDPOINT}`)
  return findOrders(data)
}

export async function getPublishedOrders({
  cursor = null,
  page = 1,
  fromDate = '',
  toDate = '',
  fromAmount = '',
  toAmount = '',
  status = '',
  salesExecutive = '',
  customerType = '',
  customer = '',
  signal,
} = {}) {
  const params = new URLSearchParams({ limit: '20' })
  if (cursor) params.set('cursor', cursor)
  else params.set('page', String(page))
  addParam(params, 'from_date', fromDate)
  addParam(params, 'to_date', toDate)
  addParam(params, 'from_amount', fromAmount)
  addParam(params, 'to_amount', toAmount)
  addParam(params, 'status', status)
  addParam(params, 'sales_executive', salesExecutive)
  addParam(params, 'customer_type', customerType)
  addParam(params, 'customer', customer)

  const data = await request(`${PUBLISHED_ORDERS_ENDPOINT}?${params}`, {
    signal,
  })
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data
  const orders = findOrders(data)
  const nextCursor = findNextCursor(data)
  const currentPage = Number(
    payload?.page ?? payload?.currentPage ?? payload?.current_page ?? page,
  )
  const totalRevenue =
    payload?.totalRevenue ??
    payload?.total_revenue ??
    data?.totalRevenue ??
    data?.total_revenue ??
    0
  const hasNext = Boolean(
    payload?.hasNext ??
    payload?.has_next ??
    data?.hasNext ??
    data?.has_next ??
    nextCursor,
  )

  return {
    orders,
    nextCursor,
    nextPage: hasNext && !nextCursor ? currentPage + 1 : null,
    currentPage,
    totalRevenue,
  }
}

export async function getTotalRevenue() {
  const data = await request(PUBLISHED_TOTAL_REVENUE_ENDPOINT)
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data

  if (typeof data === 'number' || typeof data === 'string') {
    return Number(data) || 0
  }

  return Number(
    payload?.totalRevenue ??
      payload?.total_revenue ??
      data?.totalRevenue ??
      data?.total_revenue ??
      0,
  )
}

export async function getPublishedOrderBooks(customer) {
  const data = await request(
    `${PUBLISHED_ORDER_BOOKS_ENDPOINT}/${encodeURIComponent(customer)}`,
  )
  return findBooks(data)
}

export async function deleteOrder(id) {
  await request(`${DELETE_ORDER_ENDPOINT}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function createOrder(order) {
  const data = await request(SAVE_ORDER_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(order),
  })
  console.log(data)
  return data?.data || data
}

export async function updateOrder(id, order) {
  const data = await request(
    `${UPDATE_ORDER_ENDPOINT}/${encodeURIComponent(id)}`,
    {
      method: 'PUT',
      body: JSON.stringify(order),
    },
  )
  return data?.data || data
}

export async function getOrderById(id) {
  const orderId = encodeURIComponent(id)
  const data = await request(
    `${GET_ORDER_BY_ID_ENDPOINT}/${orderId}?orderid=${orderId}`,
  )
  return data?.data || data
}

export async function changeOrderStatus(id, status, details = {}) {
  const data = await request(
    `${CHANGE_ORDER_STATUS_ENDPOINT}/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ status, ...details }),
    },
  )
  return data?.data || data
}

export async function changeOrderCustomer(id, customerId) {
  const data = await request(
    `${CHANGE_ORDER_CUSTOMER_ENDPOINT}/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ customerid: customerId }),
    },
  )
  return data?.data || data
}

export async function getOrderPayments(orderId) {
  const data = await request(
    `${GET_ORDER_PAYMENTS_ENDPOINT}/${encodeURIComponent(orderId)}`,
  )
  return data?.data || data
}

export async function getOrderReturns(orderId) {
  const data = await request(
    `${GET_ORDER_RETURNS_ENDPOINT}/${encodeURIComponent(orderId)}`,
  )
  return data?.data || data
}
export async function getOrderPaymentPlan(orderId) {
  const data = await request(
    `${GET_ORDER_PAYMENT_PLAN_ENDPOINT}/${encodeURIComponent(orderId)}`,
  )
  return data?.data || data
}

export async function addOrderPaymentPlan(orderId, paymentPlan) {
  const data = await request(
    `${ADD_ORDER_PAYMENT_PLAN_ENDPOINT}/${encodeURIComponent(orderId)}`,
    {
      method: 'POST',
      body: JSON.stringify(paymentPlan),
    },
  )
  return data?.data || data
}

export async function addOrderPayment(orderId, payment) {
  const data = await request(
    `${ADD_ORDER_PAYMENT_ENDPOINT}/${encodeURIComponent(orderId)}`,
    {
      method: 'POST',
      body: JSON.stringify(payment),
    },
  )
  return data?.data || data
}

export async function updateOrderPayment(paymentId, payment) {
  const data = await request(
    `${UPDATE_ORDER_PAYMENT_ENDPOINT}/${encodeURIComponent(paymentId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payment),
    },
  )
  return data?.data || data
}

export async function deleteOrderPayment(paymentId) {
  await request(
    `${DELETE_ORDER_PAYMENT_ENDPOINT}/${encodeURIComponent(paymentId)}`,
    { method: 'DELETE' },
  )
}

export async function orderRelease(orderId, details = {}) {
  const data = await request(
    `${RELEASE_ORDER_ENDPOINT}/${encodeURIComponent(orderId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(details),
    },
  )
  return data?.data || data
}

export async function orderReturn(orderId, details = {}) {
  const data = await request(
    `${RETURN_ORDER_ENDPOINT}/${encodeURIComponent(orderId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(details),
    },
  )
  return data?.data || data
}
